import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  updateCartItemQuantity,
  removeCartItem,
  clearCart,
  WHATSAPP_NUMBER,
  INSTAGRAM_HANDLE,
} from '../store/cartSlice';
import Navbar from '../Components/Navbar';
import Footer from '../Components/Footer';
import { Link } from 'react-router-dom';
import { FiMinus, FiPlus, FiTrash2, FiShoppingCart } from 'react-icons/fi';
import { FaWhatsapp, FaInstagram } from 'react-icons/fa';
import { toast } from 'react-toastify';

// ─── Empty Cart ───────────────────────────────────────────────────────────────
const EmptyCart = () => (
  <div className="flex flex-col min-h-screen">
    <Navbar />
    <div className="flex-grow flex items-center justify-center py-24">
      <div className="text-center">
        <svg
          className="mx-auto mb-5"
          width="90"
          height="90"
          viewBox="0 0 90 90"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ opacity: 0.6 }}
        >
          <path
            d="M15 15h9l5.4 27M49.5 49.5h-27L18 22.5h54"
            stroke="#ADB5BD"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx="30" cy="67.5" r="4.5" stroke="#ADB5BD" strokeWidth="3" />
          <circle cx="66.5" cy="67.5" r="4.5" stroke="#ADB5BD" strokeWidth="3" />
        </svg>
        <h2 className="text-2xl font-display font-normal tracking-tight text-gray-900 mb-2">Your cart is empty</h2>
        <p className="text-clay mb-6">Looks like you haven't added anything to your cart yet</p>
        <Link
          to="/"
          className="inline-block bg-ink text-cream hover:bg-brand-500 hover:text-ink font-medium py-2.5 px-10 rounded-xl transition-colors duration-200"
        >
          Continue Shopping
        </Link>
      </div>
    </div>
    <Footer />
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const CartPage = () => {
  const dispatch = useDispatch();
  const { items: cartItems = [], totalCartPrice = 0 } = useSelector((state) => state.cart);

  const [removingId, setRemovingId] = useState(null);

  const handleUpdateQuantity = (cartItemId, newQuantity) => {
    if (newQuantity > 0) {
      dispatch(updateCartItemQuantity({ cartItemId, quantity: newQuantity }));
    }
  };

  const handleRemoveItem = (cartItemId) => {
    setRemovingId(cartItemId);
    dispatch(removeCartItem(cartItemId));
    toast.success('Item removed from cart');
    setRemovingId(null);
  };

  // Build WhatsApp order message
  const buildWhatsAppMessage = () => {
    let msg = 'Hello! I would like to order the following:\n\n';
    cartItems.forEach((item) => {
      msg += `• ${item.product?.name || 'Product'} x${item.quantity}`;
      if (item.leftEyePower !== 0 || item.rightEyePower !== 0) {
        const fmtPower = (p) => {
          const n = parseFloat(p);
          if (n === 0) return 'Plano';
          return n > 0 ? `+${n.toFixed(2)}` : n.toFixed(2);
        };
        msg += ` (OD: ${fmtPower(item.rightEyePower)}, OS: ${fmtPower(item.leftEyePower)})`;
      }
      msg += '\n';
    });
    return encodeURIComponent(msg);
  };

  const handleWhatsApp = () => {
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${buildWhatsAppMessage()}`;
    window.open(url, '_blank');
  };

  const handleInstagram = () => {
    window.open(`https://www.instagram.com/${INSTAGRAM_HANDLE}/`, '_blank');
  };

  if (!cartItems || cartItems.length === 0) {
    return <EmptyCart />;
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <div className="container mx-auto px-4 py-8 flex-grow">
        <h1 className="text-2xl font-display font-normal tracking-tight mb-8">Shopping Cart</h1>
        <div className="flex flex-col lg:flex-row gap-8">

          {/* ── Cart Items ── */}
          <div className="lg:w-2/3">
            {cartItems.map((item) => (
              <div
                key={item._id}
                className="flex flex-col sm:flex-row gap-4 p-4 mb-4 bg-white rounded-2xl border border-sand shadow-card hover:shadow-card-hover"
              >
                {/* Image + basic info row on mobile */}
                <div className="flex gap-4">
                  <img
                    src={item?.product?.images?.[0]}
                    alt={item?.product?.name}
                    className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded flex-shrink-0"
                  />
                  <div className="flex-grow min-w-0">
                    <h3 className="font-semibold text-base sm:text-lg mb-1 line-clamp-2">
                      {item?.product?.name || 'Product Name Unavailable'}
                    </h3>
                    <p className="text-gray-600 text-sm mb-1.5">
                      EGP {(item?.product?.price || 0).toFixed(2)}
                    </p>

                    {/* Eye powers — compact inline badges */}
                    {(item?.leftEyePower !== 0 || item?.rightEyePower !== 0) && (() => {
                      const fmt = (p) => {
                        const n = parseFloat(p);
                        if (n === 0) return 'Plano';
                        return n > 0 ? `+${n.toFixed(2)}` : n.toFixed(2);
                      };
                      return (
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="inline-flex items-center gap-1 bg-brand-100 border border-brand-300 text-brand-700 text-xs font-medium px-2 py-0.5 rounded-full">
                            <span className="text-brand-500">OD</span> {fmt(item.rightEyePower)}
                          </span>
                          <span className="inline-flex items-center gap-1 bg-brand-100 border border-brand-300 text-brand-700 text-xs font-medium px-2 py-0.5 rounded-full">
                            <span className="text-brand-500">OS</span> {fmt(item.leftEyePower)}
                          </span>
                        </div>
                      );
                    })()}

                    {/* Total + remove — mobile only */}
                    <div className="flex items-center justify-between sm:hidden mt-2">
                      <p className="font-semibold">
                        EGP {((item?.product?.price || 0) * (item?.quantity || 1)).toFixed(2)}
                      </p>
                      <button
                        onClick={() => handleRemoveItem(item._id)}
                        disabled={removingId === item._id}
                        className="text-red-500 p-2 hover:bg-red-50 rounded"
                        aria-label="Remove item"
                      >
                        <FiTrash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Quantity + total row */}
                <div className="flex items-center justify-between sm:justify-start sm:gap-6 sm:flex-grow">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleUpdateQuantity(item._id, Math.max(1, item.quantity - 1))}
                      disabled={item.quantity <= 1}
                      aria-label="Decrease quantity"
                      className="p-2.5 sm:p-2 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-opacity"
                    >
                      <FiMinus size={16} />
                    </button>
                    <span className="mx-2 min-w-[2rem] text-center font-medium">{item?.quantity || 1}</span>
                    <button
                      onClick={() => handleUpdateQuantity(item._id, item.quantity + 1)}
                      aria-label="Increase quantity"
                      className="p-2.5 sm:p-2 hover:bg-gray-100 rounded transition-colors"
                    >
                      <FiPlus size={16} />
                    </button>
                  </div>

                  {/* Total + remove — desktop only */}
                  <div className="hidden sm:flex items-center gap-4 ml-auto">
                    <p className="font-semibold text-lg">
                      EGP {((item?.product?.price || 0) * (item?.quantity || 1)).toFixed(2)}
                    </p>
                    <button
                      onClick={() => handleRemoveItem(item._id)}
                      disabled={removingId === item._id}
                      className="text-red-500 p-2 hover:bg-red-50 rounded"
                      aria-label="Remove item"
                    >
                      <FiTrash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ── Order Summary ── */}
          <div className="lg:w-1/3">
            <div className="bg-white p-6 rounded-2xl border border-sand shadow-card sticky top-4">
              <h2 className="text-xl font-display font-normal tracking-tight mb-4">Order Summary</h2>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between">
                  <span>Subtotal ({cartItems.reduce((n, i) => n + i.quantity, 0)} items)</span>
                  <span>EGP {totalCartPrice.toFixed(2)}</span>
                </div>
                <div className="border-t border-sand pt-3 mt-3">
                  <div className="flex justify-between font-display font-normal tracking-tight text-lg">
                    <span>Total</span>
                    <span>EGP {totalCartPrice.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <p className="text-sm text-clay mb-4 text-center">
                To place your order, contact us via:
              </p>

              {/* WhatsApp */}
              <button
                onClick={handleWhatsApp}
                className="w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white rounded-md py-3 font-medium transition-colors mb-3"
              >
                <FaWhatsapp size={22} />
                Order via WhatsApp
              </button>

              {/* Instagram */}
              <button
                onClick={handleInstagram}
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-brand-600 via-brand-500 to-brand-300 hover:opacity-90 text-white rounded-xl py-3 font-medium transition-opacity"
              >
                <FaInstagram size={22} />
                Order via Instagram
              </button>

              <button
                onClick={() => dispatch(clearCart())}
                className="w-full mt-4 text-sm text-gray-400 hover:text-red-500 transition-colors"
              >
                Clear cart
              </button>
            </div>
          </div>

        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CartPage;
